import { Fragment, useCallback, useEffect, useState } from 'react';

import johnDeereLogo from './assets/john-deere.svg';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import { INTEGRATIONS, TEST_ID, TOAST_DURATION_MS } from './constants';
import type { FunctionComponent, Integration, Props } from './types';

import styles from './styles.module.scss';

const ConnectionsPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const { connect, disconnect, isConnected } = useData();
    const t = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isToastOpen, setIsToastOpen] = useState(false);

    const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
    const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

    const handleAuthorize = useCallback(async () => {
        setIsModalOpen(false);
        setIsToastOpen(true);
        await connect();
    }, [connect]);

    useEffect(() => {
        if (!isToastOpen) {
            return;
        }
        const timerId = window.setTimeout(() => setIsToastOpen(false), TOAST_DURATION_MS);

        return () => window.clearTimeout(timerId);
    }, [isToastOpen]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }

        const handleKey = (keyEvent: KeyboardEvent) => {
            if (keyEvent.key === 'Escape') {
                setIsModalOpen(false);
            }
        };

        window.addEventListener('keydown', handleKey);

        return () => window.removeEventListener('keydown', handleKey);
    }, [isModalOpen]);

    const renderCard = (integration: Integration) => {
        const isJd = integration.id === 'jd';
        const isJdConnected = isJd && isConnected;
        const cardReadyClass = isJd && !isJdConnected ? styles.cardReady : '';
        const cardConnectedClass = isJdConnected ? styles.cardConnected : '';

        const pillText = isJdConnected
            ? t.CONNECTED_SYNCING
            : isJd
              ? t.READY_TO_CONNECT
              : integration.statusLabelKey
                ? t.translate(integration.statusLabelKey)
                : '';
        const pillClass = isJdConnected
            ? styles.statusPillConnected
            : isJd
              ? styles.statusPillReady
              : styles.statusPillSoon;

        const buttonText = isJdConnected ? t.DISCONNECT : isJd ? t.CONNECT : t.NOTIFY_ME;
        const buttonClass = isJdConnected
            ? styles.buttonGhost
            : isJd
              ? styles.buttonPrimary
              : styles.buttonDisabled;
        const buttonDisabled = !isJd;
        const buttonOnClick = isJdConnected ? disconnect : isJd ? handleOpenModal : undefined;

        return (
            <article
                className={`${styles.card} ${cardReadyClass} ${cardConnectedClass}`}
                data-testid={`connectionCard-${integration.id}`}
                key={integration.id}>
                {isJd && !isJdConnected && <span className={styles.ribbon}>{t.LIVE}</span>}

                <div className={styles.cardHead}>
                    <div className={`${styles.brandMark} ${styles[integration.brandClass]}`}>
                        {integration.iconSrc ? (
                            <img alt='' className={styles.brandLogo} src={integration.iconSrc} />
                        ) : (
                            <div className={styles.brandWordmark}>
                                {integration.wordmark.map((line, lineIndex) => (
                                    <Fragment key={`${integration.id}-line-${lineIndex}`}>
                                        {lineIndex > 0 && <br />}

                                        {line}
                                    </Fragment>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={styles.cardHeadText}>
                        <h3 className={styles.cardName}>{integration.name}</h3>

                        <span className={styles.cardKind}>{t.translate(integration.kindKey)}</span>
                    </div>
                </div>

                <p className={styles.cardBody}>{t.translate(integration.descriptionKey)}</p>

                <div className={styles.featureRow}>
                    {integration.features.map(feature => (
                        <span className={styles.chip} key={`${integration.id}-chip-${feature}`}>
                            {feature}
                        </span>
                    ))}
                </div>

                <div className={styles.cardFoot}>
                    <span className={`${styles.statusPill} ${pillClass}`}>
                        <span className={styles.statusDot} />

                        {pillText}
                    </span>

                    <button
                        className={`${styles.button} ${buttonClass}`}
                        disabled={buttonDisabled}
                        onClick={buttonOnClick}
                        type='button'>
                        {buttonText}
                    </button>
                </div>
            </article>
        );
    };

    return (
        <main className={styles.connectionsPage} data-testid={testId}>
            <section className={styles.pageHead}>
                <h1 className={styles.pageTitle}>{t.PAGE_TITLE}</h1>

                <div className={styles.pageMeta}>
                    <span>{t.INTEGRATIONS_TOTAL}</span>

                    <span className={styles.metaSeparator}>·</span>

                    <span className={styles.metaEmphasis}>{t.INTEGRATIONS_LIVE}</span>

                    <span className={styles.metaSeparator}>·</span>

                    <span>{t.ZERO_CONNECTED}</span>
                </div>
            </section>

            <div className={styles.sectionHead}>
                <span className={styles.sectionHint}>{t.SECTION_HINT}</span>
            </div>

            <div className={styles.cardGrid}>{INTEGRATIONS.map(renderCard)}</div>

            {isModalOpen && (
                <div className={styles.modal} onClick={handleCloseModal} role='presentation'>
                    <div
                        className={styles.modalPanel}
                        onClick={clickEvent => clickEvent.stopPropagation()}
                        role='dialog'>
                        <div className={styles.modalHead}>
                            <div className={`${styles.brandMark} ${styles.brandMarkSmall}`}>
                                <img alt='' className={styles.brandLogo} src={johnDeereLogo} />
                            </div>

                            <h2 className={styles.modalTitle}>{t.MODAL_TITLE}</h2>

                            <button
                                aria-label={t.CANCEL}
                                className={styles.modalClose}
                                onClick={handleCloseModal}
                                type='button'>
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <p>{t.MODAL_BODY}</p>

                            <ol className={styles.modalList}>
                                <li>
                                    <span className={styles.modalStepNumber}>1</span>

                                    <span>{t.MODAL_STEP_1}</span>
                                </li>

                                <li>
                                    <span className={styles.modalStepNumber}>2</span>

                                    <span>{t.MODAL_STEP_2}</span>
                                </li>

                                <li>
                                    <span className={styles.modalStepNumber}>3</span>

                                    <span>{t.MODAL_STEP_3}</span>
                                </li>

                                <li>
                                    <span className={styles.modalStepNumber}>4</span>

                                    <span>{t.MODAL_STEP_4}</span>
                                </li>
                            </ol>
                        </div>

                        <div className={styles.modalFoot}>
                            <button
                                className={`${styles.button} ${styles.buttonGhost}`}
                                onClick={handleCloseModal}
                                type='button'>
                                {t.CANCEL}
                            </button>

                            <button
                                className={`${styles.button} ${styles.buttonPrimary}`}
                                onClick={handleAuthorize}
                                type='button'>
                                {t.AUTHORIZE}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isToastOpen && (
                <div className={styles.toast} role='status'>
                    <span className={styles.toastCheck}>
                        <svg
                            fill='none'
                            height='16'
                            stroke='currentColor'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2.4'
                            viewBox='0 0 24 24'
                            width='16'>
                            <path d='M5 12l5 5L20 7' />
                        </svg>
                    </span>

                    <span>{t.TOAST_CONNECTED}</span>
                </div>
            )}
        </main>
    );
};

export default ConnectionsPage;
